import { computed, observable } from 'mobx';
import Leg, { convertLegResponseToArgs, type LegResponseArgs } from './leg';
import type { LegSummaryResponseArgs } from './leg_summary';
import LegSummary, { convertLegSummaryResponseToArgs } from './leg_summary';
import type { SegmentTerminalResponseArgs } from './segment_terminal';
import SegmentTerminal, { convertSegmentTerminalResponseToArgs } from './segment_terminal';

type SegmentType = 'air' | 'shin' | 'express' | 'evo_air';

export type SegmentResponseArgs = {
  legs: LegResponseArgs[];
  type?: SegmentType;
  leg_summaries?: LegSummaryResponseArgs[];
  boarding?: SegmentTerminalResponseArgs;
  arrival?: SegmentTerminalResponseArgs;
  distance?: string | null;
  route_time?: string | null;
};

export type SegmentArgs = {
  legs: Leg[];
  type?: SegmentType;
  legSummaries?: LegSummary[];
  boarding?: SegmentTerminal;
  arrival?: SegmentTerminal;
  distance?: string | null;
  routeTime?: string | null;
};

export const convertSegmentResponseToArgs = (response: SegmentResponseArgs) => {
  const args: SegmentArgs = {
    legs: response.legs.map(raw => new Leg(convertLegResponseToArgs(raw))),
    type: response.type,
    legSummaries: response.leg_summaries?.map(raw => new LegSummary(convertLegSummaryResponseToArgs(raw))),
    boarding: response.boarding
      ? new SegmentTerminal(convertSegmentTerminalResponseToArgs(response.boarding))
      : undefined,
    arrival: response.arrival
      ? new SegmentTerminal(convertSegmentTerminalResponseToArgs(response.arrival))
      : undefined,
    distance: response.distance,
    routeTime: response.route_time
  };
  return args;
};

export default class Segment {
  @observable legs: Leg[];
  @observable type?: SegmentType;
  @observable legSummaries?: LegSummary[];
  @observable boarding?: SegmentTerminal;
  @observable arrival?: SegmentTerminal;
  @observable distance?: string | null;
  @observable routeTime?: string | null;

  constructor(args: SegmentArgs) {
    this.legs = args.legs;
    this.type = args.type;
    this.legSummaries = args.legSummaries;
    this.boarding = args.boarding;
    this.arrival = args.arrival;
    this.distance = args.distance;
    this.routeTime = args.routeTime;
  }

  @computed get isAir() {
    return this.type === 'air' || this.type === 'evo_air';
  }

  @computed get shouldShowBoardingStation(): boolean {
    return !!(this.boarding?.name && this.boarding.name !== this.legs[0].from.name);
  }

  @computed get shouldShowArrivalStation(): boolean {
    return !!(this.arrival?.name && this.arrival.name !== this.legs[this.legs.length - 1].to.name);
  }

  static imagePathByName(name: string | null): string {
    if (name) {
      const legname = name.replace('ＪＲ', 'JR');
      return utils.transitIconPath(legname);
    }
    return '';
  }
}
